/**
 * Minimal hash-based router (Vanilla JS).
 *
 * Hash routing keeps the app a pure static SPA — no server-side rewrites are
 * needed, which suits a PWA served from any host / packaged by PWABuilder.
 */

const routes = [];
let notFoundHandler = null;

/* -------------------------------------------------------------------------- */
/* History entries & scroll memory                                            */
/* -------------------------------------------------------------------------- */

/**
 * Hash navigations carry no `history.state`, so every entry gets stamped with a
 * monotonic index the first time we render it. That index is what lets us
 *   - tell whether the entry behind us is one of ours ({@link canGoBack}), and
 *   - remember a scroll position per history entry rather than per route, so
 *     going back to a list lands where the user left it.
 */
const ENTRY_KEY = 'appEntry';

let lastEntry = 0; // highest index handed out during this page load
let currentEntry = 0; // index of the entry being rendered
const scrollByEntry = new Map();
let restorePending = false;

function stampedIndex() {
  const index = window.history.state?.[ENTRY_KEY];
  return Number.isInteger(index) ? index : null;
}

/** Give the current history entry an index, keeping the one it already has. */
function stampEntry() {
  const existing = stampedIndex();
  if (existing !== null) {
    currentEntry = existing;
    lastEntry = Math.max(lastEntry, existing);
    return;
  }
  currentEntry = lastEntry + 1;
  lastEntry = currentEntry;
  window.history.replaceState({ ...window.history.state, [ENTRY_KEY]: currentEntry }, '');
}

/**
 * True when the previous history entry is one of our screens: indices start at
 * 1, so anything above that was pushed on top of an app entry. Survives a
 * reload, since the stamp lives in the entry's own `history.state`.
 */
export function canGoBack() {
  return currentEntry > 1;
}

/**
 * The app's "back": pop the history entry — which brings the previous screen
 * and its scroll position back — or, on a cold start with nothing of ours
 * behind us (a deep link straight into a sub-screen), navigate to `fallback`.
 * @param {string} [fallback='/'] Route to use when there is no entry to pop.
 */
export function goBack(fallback = '/') {
  if (canGoBack()) window.history.back();
  else navigate(fallback);
}

/**
 * Put the scroll position back where it was when this history entry was left.
 *
 * Views call it once their content is in the DOM — the browser can't do it for
 * us because every screen renders asynchronously (IndexedDB / network), so at
 * navigation time the document is still just a loader. Only the first call
 * after a `render()` scrolls, so a retry-driven re-render never yanks the page.
 * Content can keep growing (thumbnails, async rows), so the target is
 * re-applied over a few frames until the document is tall enough to reach it.
 */
export function restoreScroll() {
  if (!restorePending) return;
  restorePending = false;
  const target = scrollByEntry.get(currentEntry) || 0;
  if (!target) return;

  let frames = 0;
  const apply = () => {
    window.scrollTo(0, target);
    if (Math.abs(window.scrollY - target) < 2 || frames++ > 10) return;
    requestAnimationFrame(apply);
  };
  requestAnimationFrame(apply);
}

/**
 * Register a route. `path` may contain `:name` segments (e.g. `/chapter/:id`);
 * the matched values are passed to the handler as a params object.
 * @param {string} path
 * @param {(params: Record<string, string>) => void} handler
 */
export function route(path, handler) {
  const keys = [];
  const pattern = path.replace(/:([^/]+)/g, (_, key) => {
    keys.push(key);
    return '([^/]+)';
  });
  routes.push({ regex: new RegExp(`^${pattern}$`), keys, handler });
}

export function setNotFound(handler) {
  notFoundHandler = handler;
}

function currentPath() {
  const hash = window.location.hash.replace(/^#/, '');
  return hash || '/';
}

export function navigate(path) {
  if (currentPath() === path) {
    render(); // same route — re-render explicitly
  } else {
    window.location.hash = path; // triggers `hashchange` → render()
  }
}

function render() {
  stampEntry();

  // An entry we're seeing for the first time starts at the top; one we're
  // returning to keeps its scroll until the view calls `restoreScroll()`.
  const saved = scrollByEntry.get(currentEntry) || 0;
  restorePending = saved > 0;
  if (!restorePending) window.scrollTo(0, 0);

  const path = currentPath();
  for (const { regex, keys, handler } of routes) {
    const match = regex.exec(path);
    if (!match) continue;
    const params = {};
    keys.forEach((key, i) => {
      params[key] = decodeURIComponent(match[i + 1]);
    });
    return handler(params);
  }
  if (notFoundHandler) notFoundHandler();
}

export function startRouter() {
  // We restore scroll ourselves, per history entry, once a view's content is in
  // the DOM (see `restoreScroll`), so keep the browser out of it.
  if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

  // Track the current screen's scroll position continuously: by the time
  // `hashchange` fires, the hash — and the scroll — have already moved, so it is
  // too late to read it there. Paused while a restore is pending so the
  // browser's own jump-to-top on a hash change can't overwrite a saved position.
  window.addEventListener(
    'scroll',
    () => {
      if (!restorePending) scrollByEntry.set(currentEntry, window.scrollY);
    },
    { passive: true }
  );

  window.addEventListener('hashchange', render);
  render();
}
